import { DecisionGraph, sessionGraphWithRelease } from "@imminently/decisively-core";

const legacyJson = (legacy: unknown): any => {
  if (typeof legacy === "string") {
    try {
      return JSON.parse(legacy);
    } catch {
      return undefined;
    }
  }
  return legacy && typeof legacy === "object" && Array.isArray((legacy as any).edges) ? legacy : undefined;
};

/**
 * A legacy saved graph (sessions and reports saved before decision graphs) read with its release's rule graph, as
 * the legacy graph was built for views: each node the rule node with the saved values over it, a global copy of the
 * template behind each instance (views check its `hidden`), and unless the saved graph has `noEdges`, the rule
 * graph's edges plus the instance edges compressed into the saved edges' labels.
 */
export const legacyGraphWithRelease = (legacy: unknown, ruleGraph: unknown): DecisionGraph => {
  const graph = sessionGraphWithRelease(legacy, ruleGraph);
  const rules = ruleGraph ? DecisionGraph.from(ruleGraph as any) : undefined;
  if (!rules || rules.nodeCount() === 0) return graph;

  const listed = (path: string) => {
    const id = graph.ref(path);
    return id !== undefined && graph.isListed(id) ? id : undefined;
  };

  // the templates' global copies
  for (const id of graph.ids()) {
    if (graph.isGlobal(id)) continue;
    const guid = graph.guid(id);
    if (listed(guid) !== undefined) continue;
    const ruleId = rules.ref(guid);
    const ruleNode = ruleId === undefined ? undefined : rules.node(ruleId);
    if (!ruleNode) continue;
    // a template only reached as an edge target becomes a node of its own: its edges move to it
    const target = graph.ref(guid);
    const added = graph.setNode(graph.createRef(guid), { ...ruleNode });
    if (target !== undefined && target !== added) {
      for (const from of [...graph.predecessors(target)]) graph.removeEdge(from, target).setEdge(from, added);
    }
  }

  const json = legacyJson(legacy);
  const label = json?.value;
  if (label?.noEdges === true) return graph;

  const link = (v: string, w: string) => {
    const from = listed(v);
    const to = graph.ref(w);
    if (from !== undefined && to !== undefined) graph.setEdge(from, to);
  };
  for (const id of rules.ids()) {
    const v = rules.deref(id);
    for (const to of rules.successors(id)) link(v, rules.deref(to));
  }
  const instancePaths = label?.instancePaths as Record<string, string> | undefined;
  if (instancePaths) {
    for (const edge of json?.edges ?? []) {
      const instances = edge?.value?.instances as Record<string, string[]> | undefined;
      if (!instances) continue;
      for (const [instancePath, targets] of Object.entries(instances)) {
        for (const target of targets) {
          link(
            instancePath === "global" ? edge.v : `${instancePaths[instancePath]}/${edge.v}`,
            target === "global" ? edge.w : `${instancePaths[target]}/${edge.w}`,
          );
        }
      }
    }
  }
  return graph;
};
